import type { CompositionTemplate } from './types';

/**
 * The Graphics template grid splits every category's templates into two
 * user-facing groups by `isGlobalPreset`: the workspace's own templates
 * ("Workspace Templates") and the built-in Arbor presets ("Arbor Templates").
 * These are surfaced as filter chips and — since they're the grouping a user
 * actually navigates — as the second breadcrumb segment.
 */
export const WORKSPACE_TEMPLATES_LABEL = 'Workspace Templates';
export const ARBOR_TEMPLATES_LABEL = 'Arbor Templates';

export type TemplateChip = 'my' | 'arbor';

export const templateGroupLabel = (isArbor: boolean): string =>
  isArbor ? ARBOR_TEMPLATES_LABEL : WORKSPACE_TEMPLATES_LABEL;

/**
 * Resolves which template group is effectively shown for a list of
 * compositions, given the user's chip selection. Mirrors the long-standing
 * logic: honour the picked chip when that group has compositions, else fall
 * back to whichever group exists (preferring Arbor). Shared by the grid (chip
 * view) and the breadcrumb so they never disagree.
 */
export function resolveTemplateGroup(
  compositions: CompositionTemplate[],
  activeChip: TemplateChip,
  hideGlobalPresets: boolean | undefined,
): { effectiveChip: TemplateChip; hasMy: boolean; hasArbor: boolean } {
  const hasMy = compositions.some(c => !c.isGlobalPreset);
  const hasArbor =
    !hideGlobalPresets && compositions.some(c => c.isGlobalPreset);
  const effectiveChip: TemplateChip =
    activeChip === 'my' && hasMy ? 'my' : hasArbor ? 'arbor' : 'my';
  return { effectiveChip, hasMy, hasArbor };
}
